import { useEffect, useState } from 'react'
import { utils } from '@chordcommerce/gatsby-theme-autonomy'
import { useCanClub } from '~/hooks/components/use-can-club'
import { PROMO_STORAGE } from '~/hooks/components/use-promo-codes'
import storage from '~/services/storage'
import { DISCOUNTS } from '~/utils/lists/club-discounts'
import { getDiscountedPrice } from '~/utils/packs'
import { getAllCartPromotions } from '~/utils/promotions'

const SITEWIDE_DISCOUNT = process.env.GATSBY_SITEWIDE_DISCOUNT

const SITEWIDE_DISCOUNT_CLUB = process.env.GATSBY_SITEWIDE_DISCOUNT_CLUB

export function usePostPromoPrice({
  order,
  postPromoPrice,
  postPromoPrice24,
  postPromoPriceCodes,
  price,
}) {
  const { clubDiscount } = useCanClub()
  const [displayPrice, setDisplayPrice] = useState(null)

  useEffect(() => {
    if (SITEWIDE_DISCOUNT_CLUB && clubDiscount) {
      setDisplayPrice(getDiscountedPrice(SITEWIDE_DISCOUNT_CLUB, price))
    } else if (SITEWIDE_DISCOUNT) {
      setDisplayPrice(getDiscountedPrice(SITEWIDE_DISCOUNT, price))
    } else if (postPromoPrice24 && clubDiscount === DISCOUNTS.CLUB_24) {
      setDisplayPrice(postPromoPrice24)
    } else if (postPromoPriceCodes) {
      const bestCodePrice = getBestCodePostPromoPrice(
        postPromoPriceCodes,
        order
      )

      if (bestCodePrice && (!displayPrice || bestCodePrice < displayPrice)) {
        setDisplayPrice(bestCodePrice)
      }
    } else if (postPromoPrice) {
      setDisplayPrice(postPromoPrice)
    }
  }, [
    clubDiscount,
    displayPrice,
    order,
    postPromoPrice,
    postPromoPrice24,
    postPromoPriceCodes,
    price,
  ])

  return displayPrice
}

// Determine if any of the codePrices codes have been or will be applied to the
// cart. If so, return the best one. This is helpful because the front end isn't
// aware of promo code eligibility, but by passing only codes that are sure to
// apply, we can show the customer a discounted price on the add to cart button.
function getBestCodePostPromoPrice(codePrices, order) {
  const activeCodes = [
    ...getCartPromoCodes(order),
    ...getStoragePromoCodes(),
    ...getUrlPromoCodes(),
  ]

  return Math.min(
    ...Object.keys(codePrices)
      .filter((code) => activeCodes.includes(code))
      .map((code) => codePrices[code])
  )
}

// Find codes that have already been applied to the cart.
function getCartPromoCodes(order) {
  const cartPromos = getAllCartPromotions(order)

  if (cartPromos?.length > 0) {
    return cartPromos.map((cartPromo) => cartPromo.code?.toLowerCase())
  }

  return []
}

// Find codes that were stored and will get applied to the cart.
function getStoragePromoCodes() {
  const storageCodes = storage.getItem(PROMO_STORAGE)

  if (storageCodes?.length > 0) {
    return storageCodes.split(' ')
  }

  return []
}

// Find codes in the URL that will get stored (initial page load).
function getUrlPromoCodes() {
  const { getParam } = utils
  // @todo Deprecate aff due to collisions with Goaffpro attribution.
  const urlCodes = getParam('mwaff') || getParam('aff') || getParam('promo')

  if (urlCodes?.length > 0) {
    return urlCodes.split(' ')
  }

  return []
}
